import React, { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { useLocalStorage } from 'react-use';
import { useCurrentUserInfo } from '@hooks/useCurrentUserInfo';
import { useUser, LoginState } from '@hooks/useUser';
import { Loading } from '@components/shared/Loading';
import { login } from '@lib/auth';
import { ATLAS_REDIRECT_LOCAL_STORAGE_KEY } from '@helpers/constants';

export const withAuth = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
): React.FC<P> => {
  return (props: P) => {
    // checks whether we are on client / browser or server.
    const router = useRouter();
    const [, setValue] = useLocalStorage(ATLAS_REDIRECT_LOCAL_STORAGE_KEY);
    const { loginState } = useUser();
    const loggedIn = loginState === LoginState.LOGGEDIN;
    const isLoading = loginState === LoginState.AWAITING_LOGIN;
    const { data: userInfo } = useCurrentUserInfo();
    const hasNoFeedsOrLicensors =
      userInfo && isEmpty(userInfo.feeds) && isEmpty(userInfo.licensors);
    const isUnauthorized = userInfo && !userInfo.hasAccess;

    useEffect(() => {
      if (!isLoading && !loggedIn) {
        // if the user was trying to access a protected route before being logged in
        // we need to save the path they were trying to access in local storage
        // so we can redirect the user in callback page after logging in
        if (router.asPath) {
          setValue(encodeURIComponent(router.asPath));
        }
        login();
      }

      if (loggedIn && isUnauthorized) {
        router.replace(`/unauthorized`);
      }

      if (loggedIn && hasNoFeedsOrLicensors) {
        router.replace('/noassociation');
      }

      // Excludes router from list to prevent an infinite loop bug caused by calling push within a useEffect
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isUnauthorized, loggedIn, setValue, hasNoFeedsOrLicensors]);

    if (isLoading || !loggedIn || isUnauthorized) {
      return <Loading data-screen="auth" />;
    }

    // If logged in we just render the component that was passed with all its props
    return <WrappedComponent {...props} />;
  };
};

export default withAuth;
